/**
 * Sentry event scrubber — keeps project context, auth tokens, and PII out of
 * the events the SDK ships to the wire.
 *
 * Mirrors the backend rules in ``backend/app/utils/observability.py`` so the
 * two halves of the stack agree on what counts as sensitive. If you add a
 * new field name here, add the equivalent fragment to ``SENSITIVE_KEY_FRAGMENTS``
 * on the backend.
 *
 * Privacy hard rule (set 2026-05-12): never send to Sentry:
 *   - raw project context, task output, or provider artifact bytes
 *   - workspace_context body / initial_context
 *   - auth tokens, API keys, refresh tokens, Logto session cookies
 *   - end-user emails, phone numbers, full names, IP addresses
 */

import type { ErrorEvent, EventHint, Event, Breadcrumb } from '@sentry/react'

export const REDACTED = '[REDACTED]'

/** Case-insensitive substring fragments. If any fragment appears in a key
 *  name, the value is wiped entirely. Substring match catches variants like
 *  `X-Access-Token`, `user_email`, `initial_context` without enumeration. */
const SENSITIVE_KEY_FRAGMENTS = [
  // credentials / auth
  'password',
  'passwd',
  'secret',
  'token',
  'apikey',
  'api_key',
  'authorization',
  'auth',
  'cookie',
  'jwt',
  'session',
  'bearer',
  'credential',
  // connection strings
  'dsn',
  'database_url',
  'conn_str',
  'connection_string',
  // PII
  'email',
  'phone',
  'ssn',
  'address',
  // user/project content
  'raw_text',
  'task_output',
  'project_context',
  'file_contents',
  'file_bytes',
  'body',
  'content',
  'initial_context',
  'workspace_context',
  'field_values',
  'current_value',
  'field_value',
  'extracted_text',
  'page_text',
]

export function isSensitiveKey(key: string): boolean {
  const lower = key.toLowerCase()
  return SENSITIVE_KEY_FRAGMENTS.some((fragment) => lower.includes(fragment))
}

// Value-shape patterns. We scrub these inside any string we walk. The
// order matters: URL-with-userinfo runs first so the trailing `:port/...`
// isn't accidentally caught by the API-key rule.
const RE_URL_USERINFO = /\b(https?:\/\/)[^/@\s]+:[^/@\s]+@[^\s]+/g
const RE_POSTGRES_DSN = /postgres(?:ql)?(?:\+\w+)?:\/\/[^\s]+/g
const RE_BEARER = /Bearer\s+[A-Za-z0-9._+/=-]+/gi
const RE_JWT = /\b[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g
const RE_API_KEY = /\b(?:aiesc|sk|pk|api|key)_[A-Za-z0-9_-]{12,}\b/g
const RE_EMAIL = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g
const RE_PHONE = /(?:(?<=\D)|^)(?:\+?1[\s.-])?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}(?=\D|$)/g
const RE_SSN = /(?<!\d)\d{3}-\d{2}-\d{4}(?!\d)/g

export function scrubString(value: string): string {
  let out = value.replace(RE_URL_USERINFO, `$1${REDACTED}@`)
  out = out.replace(RE_POSTGRES_DSN, REDACTED)
  out = out.replace(RE_BEARER, `Bearer ${REDACTED}`)
  out = out.replace(RE_JWT, REDACTED)
  out = out.replace(RE_API_KEY, REDACTED)
  out = out.replace(RE_EMAIL, REDACTED)
  out = out.replace(RE_PHONE, REDACTED)
  out = out.replace(RE_SSN, REDACTED)
  return out
}

const MAX_DEPTH = 12

/**
 * Recursively scrub any value. Mirrors the backend ``scrub_value`` rules:
 *
 *  - dict / object — sensitive key ⇒ value replaced with REDACTED; non-sensitive
 *    key ⇒ recurse into the value.
 *  - array — recurse element-wise.
 *  - string — run ``scrubString`` for shape-based redaction.
 *  - everything else — return as-is.
 *
 * Returns ``unknown`` so the caller can cast back to the originating field
 * type explicitly (e.g. ``Record<string, unknown>``). This avoids ``as unknown``
 * gymnastics at the boundary while still keeping the scrubber generic.
 *
 * Never throws. On unexpected shapes we return the input untouched: failing
 * closed would lose debugging context for the on-call engineer.
 */
export function scrubValue(value: unknown, depth = 0): unknown {
  if (depth > MAX_DEPTH) {
    return REDACTED
  }
  if (value === null || value === undefined) return value
  if (typeof value === 'string') return scrubString(value)
  if (typeof value !== 'object') return value
  if (Array.isArray(value)) {
    return value.map((item) => scrubValue(item, depth + 1))
  }
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = isSensitiveKey(k) ? REDACTED : scrubValue(v, depth + 1)
  }
  return out
}

/**
 * Synthetic "safe kanban probe" noise the kanban orchestrator deliberately
 * throws from a HeadlessChrome session to verify the Sentry pipeline is
 * alive. These are not real product failures; the parent infra-side attempt
 * to filter them with a Sentry inbound rule failed because this project's
 * plan rejects ``options.filters:error_messages`` updates (HTTP 400
 * "You do not have that feature enabled"), so we drop them client-side
 * before ``Sentry.send`` is ever called.
 *
 * Anchored on both ends and constrained to the exact task-id alphabet so
 * a generic error containing the words "kanban" or "probe" — or a real
 * customer-facing failure that merely mentions the probe label — is NOT
 * silenced.
 */
const SAFE_KANBAN_PROBE_RE =
  /^kanban-t_[a-z0-9]+-safe-frontend-uncaught-probe$/

export function isSafeKanbanProbeEvent(event: Event): boolean {
  if (
    typeof event.message === 'string' &&
    SAFE_KANBAN_PROBE_RE.test(event.message)
  ) {
    return true
  }
  const values = event.exception?.values
  if (values) {
    for (const v of values) {
      if (typeof v.value === 'string' && SAFE_KANBAN_PROBE_RE.test(v.value)) {
        return true
      }
    }
  }
  return false
}

/**
 * Sentry ``beforeSend`` hook. Returns the scrubbed event or ``null`` to drop
 * the event entirely if scrubbing fails or the event is recognised synthetic
 * kanban probe noise (see :func:`isSafeKanbanProbeEvent`).
 */
export function scrubSentryEvent(
  event: ErrorEvent,
  hint?: EventHint,
): ErrorEvent | null {
  void hint
  try {
    if (isSafeKanbanProbeEvent(event)) return null
    return scrubEventLike(event) as ErrorEvent
  } catch {
    // If the scrubber blows up on a malformed event, drop it rather than
    // leak partially-scrubbed PII.
    return null
  }
}

function scrubEventLike(event: Event): Event {
  // User block: keep `id` (opaque internal id, non-PII) only.
  if (event.user && typeof event.user === 'object') {
    const next: Record<string, unknown> = {}
    const id: unknown = event.user.id
    if (typeof id === 'string' || typeof id === 'number') {
      next.id = id
    }
    event.user = next
  }

  if (event.request && typeof event.request === 'object') {
    const req = event.request as Record<string, unknown>
    const headers = req.headers
    if (headers && typeof headers === 'object') {
      const cleaned: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(headers as Record<string, unknown>)) {
        cleaned[k] = isSensitiveKey(k) ? REDACTED : scrubValue(v)
      }
      req.headers = cleaned
    }
    if ('cookies' in req && req.cookies !== undefined) {
      req.cookies = REDACTED
    }
    if ('data' in req && req.data !== undefined) {
      req.data = scrubValue(req.data)
    }
    if (typeof req.query_string === 'string') {
      req.query_string = scrubString(req.query_string)
    }
    if (typeof req.url === 'string') {
      req.url = scrubString(req.url)
    }
  }

  if (event.extra) {
    event.extra = scrubValue(event.extra) as Event['extra']
  }
  if (event.tags) {
    event.tags = scrubValue(event.tags) as Event['tags']
  }
  if (event.contexts) {
    event.contexts = scrubValue(event.contexts) as Event['contexts']
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((b) => scrubBreadcrumb(b))
  }
  if (typeof event.message === 'string') {
    event.message = scrubString(event.message)
  }
  // exception is { values: [{ type, value, stacktrace }] }
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((v) => ({
      ...v,
      value: typeof v.value === 'string' ? scrubString(v.value) : v.value,
    }))
  }
  return event
}

/**
 * Sentry ``beforeBreadcrumb`` hook. Run the same scrubber over each crumb
 * before it lands on the in-memory ring buffer — by the time ``beforeSend``
 * fires, the breadcrumb's data has already been displayed to other listeners
 * (e.g. the Sentry replay integration), so scrub at write time.
 */
export function scrubBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  const next: Breadcrumb = { ...crumb }
  if (typeof next.message === 'string') {
    next.message = scrubString(next.message)
  }
  if (next.data && typeof next.data === 'object') {
    next.data = scrubValue(next.data) as Breadcrumb['data']
  }
  return next
}
